import React from 'react';
import {
  ChartMode,
  ChartTitle,
  DrawTools,
  Share,
  SmartChart,
  StudyLegend,
  ToolbarWidget,
  Views,
} from '@deriv-com/smartcharts';
import { TGranularity } from 'src/types';

// Minimal SmartChart that renders UI but does not fetch data
export default function SmartChartNoData() {
  // Ensure legacy flutterChart drawing APIs exist before SmartChart mounts
  try {
    const w: any = window as any;
    w.flutterChart = w.flutterChart || {};
    if (typeof w.flutterChart.clearDrawingTool !== 'function') {
      w.flutterChart.clearDrawingTool = () => {};
    }
    if (typeof w.flutterChart.removeDrawingTool !== 'function') {
      w.flutterChart.removeDrawingTool = (_index: number) => {};
    }
    w.flutterChart.drawings = w.flutterChart.drawings || {};
    if (typeof w.flutterChart.drawings.clearDrawingTool !== 'function') {
      w.flutterChart.drawings.clearDrawingTool = () => {};
    }
    if (typeof w.flutterChart.drawings.removeDrawingTool !== 'function') {
      w.flutterChart.drawings.removeDrawingTool = (_index: number) => {};
    }
    // SmartChart.clearLayout expects window.flutterChart.drawingTool.clearDrawingTool()
    w.flutterChart.drawingTool = w.flutterChart.drawingTool || {};
    if (typeof w.flutterChart.drawingTool.clearDrawingTool !== 'function') {
      w.flutterChart.drawingTool.clearDrawingTool = () => {};
    }
    if (typeof w.flutterChart.drawingTool.clearDrawingToolSelect !== 'function') {
      w.flutterChart.drawingTool.clearDrawingToolSelect = () => {};
    }
    if (typeof w.flutterChart.drawingTool.removeDrawingTool !== 'function') {
      w.flutterChart.drawingTool.removeDrawingTool = (_index: number) => {};
    }
    // Config shim used by ChartAdapterStore.setSymbolClosed and others
    w.flutterChart.config = w.flutterChart.config || {};
    if (typeof w.flutterChart.config.setSymbolClosed !== 'function') {
      w.flutterChart.config.setSymbolClosed = (_isClosed: boolean) => {};
    }
    if (typeof w.flutterChart.config.setRemainingTime !== 'function') {
      w.flutterChart.config.setRemainingTime = (_time: string) => {};
    }
    if (typeof w.flutterChart.config.updateCrosshairVisibility !== 'function') {
      w.flutterChart.config.updateCrosshairVisibility = (_visible: boolean) => {};
    }
    if (typeof w.flutterChart.config.updateLiveStatus !== 'function') {
      w.flutterChart.config.updateLiveStatus = (_isLive: boolean) => {};
    }
  } catch {}
  const [symbol, setSymbol] = React.useState<string>(() => {
    try {
      return localStorage.getItem('smartcharts.last_symbol') || 'R_100';
    } catch {
      return 'R_100';
    }
  });
  const [granularity, setGranularity] = React.useState<TGranularity>(0);

  const noopReq = React.useCallback(async () => ({ echo_req: {}, msg_type: '' }), []);

  const handleSymbolChange = React.useCallback(
    (newSymbol: string) => {
      setSymbol(newSymbol);
      try {
        localStorage.setItem('smartcharts.last_symbol', newSymbol);
      } catch {}
      try {
        window.dispatchEvent(
          new CustomEvent('smartchart:selection', {
            detail: { symbol: newSymbol, granularity },
          })
        );
      } catch {}
    },
    [granularity]
  );

  const handleGranularityChange = React.useCallback(
    (timePeriod: TGranularity) => {
      setGranularity(timePeriod);
      try {
        window.dispatchEvent(
          new CustomEvent('smartchart:selection', {
            detail: { symbol, granularity: timePeriod },
          })
        );
      } catch {}
    },
    [symbol]
  );

  const handleChartTypeChange = React.useCallback(
    (_chartType?: string) => {
      // Chart type changes are handled by SmartChart UI but don't affect lightweight engine directly
      console.log('[SmartChartNoData] Chart type change:', _chartType);
    },
    []
  );

  const renderTop = React.useCallback(() => <ChartTitle onChange={handleSymbolChange} />, [handleSymbolChange]);

  const renderToolbar = React.useCallback(
    () => (
      <ToolbarWidget>
        <ChartMode onChartType={handleChartTypeChange} onGranularity={handleGranularityChange} />
        <StudyLegend />
        <Views onChartType={handleChartTypeChange} onGranularity={handleGranularityChange} />
        <DrawTools />
        <Share />
      </ToolbarWidget>
    ),
    [handleChartTypeChange, handleGranularityChange]
  );

  return (
    <SmartChart
      symbol={symbol}
      granularity={granularity}
      enableRouting
      // Pretend network is online and connection opened to bypass Loader
      networkStatus={{ class: 'online' } as any}
      isConnectionOpened={true}
      // Static chart mode disables internal loaders and data fetching
      isStaticChart={true}
      topWidgets={renderTop}
      toolbarWidget={renderToolbar}
      chartControlsWidgets={() => <div />}
      requestAPI={noopReq}
      requestSubscribe={noopReq}
      requestForget={noopReq}
      // Provide minimal chartData to bypass internal trading-times loader
      chartData={{ tradingTimes: { markets: [] } as any, activeSymbols: [] as any }}
      shouldFetchTradingTimes={false}
      shouldFetchTickHistory={false}
      isLive={true}
      enabledChartFooter={false}
      // Optionally mark chart as ready immediately
      chartStatusListener={() => true}
    />
  );
}
